/**
 * Shared per-room Centrifuge subscription registry.
 *
 * Room channels need an awaited token fetch before `centrifuge.newSubscription()`
 * can even be called, so — unlike the personal-channel registry in
 * useUserChannel.js, which keys by channel name and subscribes synchronously —
 * this keys by `roomId` (known synchronously by every caller) and guards setup
 * with a shared in-flight promise. That lets multiple callers (e.g. a global
 * "all rooms" tracker and an open Thread) subscribe to the same room
 * concurrently without racing a double token-fetch or hitting Centrifuge's
 * "Subscription to the channel ... already exists" error.
 */

const registriesByClient = new WeakMap();

export const getRoomRegistry = (centrifuge, roomId) => {
  let byRoom = registriesByClient.get(centrifuge);
  if (!byRoom) {
    byRoom = new Map();
    registriesByClient.set(centrifuge, byRoom);
  }
  let registry = byRoom.get(roomId);
  if (!registry) {
    registry = {
      sub: null,
      coachSub: null,
      channel: null,
      canPublish: false,
      listeners: new Set(),
      setupPromise: null,
      released: false,
    };
    byRoom.set(roomId, registry);
  }
  return registry;
};

const teardownSubs = (centrifuge, registry) => {
  if (registry.sub) {
    try {
      registry.sub.removeAllListeners?.();
      registry.sub.unsubscribe();
      // Unsubscribe alone leaves the subscription registered on the client,
      // which would make a later newSubscription() for this channel throw.
      centrifuge.removeSubscription?.(registry.sub);
    } catch (err) {
      console.error("Room channel cleanup failed:", err);
    }
    registry.sub = null;
  }
  if (registry.coachSub) {
    try {
      registry.coachSub.removeAllListeners?.();
      registry.coachSub.unsubscribe();
      centrifuge.removeSubscription?.(registry.coachSub);
    } catch (err) {
      // ignore — coach sub-channel is best-effort to begin with
    }
    registry.coachSub = null;
  }
};

export const releaseRoomRegistry = (centrifuge, roomId, registry) => {
  if (registry.listeners.size > 0) {
    return;
  }
  registriesByClient.get(centrifuge)?.delete(roomId);
  // If a token fetch/subscribe is still in flight, `sub` is null here and
  // there is nothing to tear down yet — the `released` flag tells
  // ensureRoomSetup to unsubscribe as soon as its setup resolves, instead of
  // leaking a live listener-less subscription for the rest of the session.
  registry.released = true;
  teardownSubs(centrifuge, registry);
};

/**
 * Ensures exactly one token fetch + subscription pair per (centrifuge, roomId),
 * even when multiple hook instances mount for the same room concurrently.
 *
 * @param {import("centrifuge").Centrifuge} centrifuge
 * @param {string|number} roomId
 * @param {Object} registry - From getRoomRegistry().
 * @param {() => Promise<{ token, channel, can_publish }>} fetchToken
 */
export const ensureRoomSetup = async (centrifuge, roomId, registry, fetchToken) => {
  if (registry.sub) {
    return;
  }
  if (!registry.setupPromise) {
    registry.setupPromise = (async () => {
      const payload = await fetchToken();
      const { token, channel, can_publish: canPublishFlag } = payload || {};
      if (!channel) {
        throw new Error("Room channel: server did not return a channel name");
      }

      const sub = centrifuge.newSubscription(channel, {
        token,
        getToken: async () => (await fetchToken())?.token,
      });
      sub.on("publication", (ctx) => {
        registry.listeners.forEach((fn) => fn(ctx.data));
      });
      sub.subscribe();

      let coachSub = null;
      try {
        coachSub = centrifuge.newSubscription(`${channel}#coach`);
        coachSub.on("publication", (ctx) => {
          registry.listeners.forEach((fn) => fn(ctx.data));
        });
        coachSub.subscribe();
      } catch (err) {
        // Non-coach or not permitted — safe to ignore.
        coachSub = null;
      }

      registry.sub = sub;
      registry.coachSub = coachSub;
      registry.channel = channel;
      registry.canPublish = Boolean(canPublishFlag);

      // Every consumer unmounted while the token fetch was in flight — the
      // registry was already dropped from the map, so clean up immediately.
      if (registry.released) {
        teardownSubs(centrifuge, registry);
      }
    })();
  }
  try {
    await registry.setupPromise;
  } finally {
    registry.setupPromise = null;
  }
};
